import React, { useState, useContext } from "react";
import { useMutation } from "@apollo/client";
import { Form, Confirm } from "semantic-ui-react";
import SimpleUserList from "./SimpleUserList";
import { INVITE_USER, REMOVE_PARENT, getStudentInput } from "../GraphQL";
import UserContext from "contexts/UserContext";
import gql from "graphql-tag";

const frequencies = [
  { key: "f", text: "Daily", value: "daily" },
  { key: "w", text: "Weekly", value: "weekly" },
];

const StudentForm = ({
  student,
  onCancel,
  onDelete,
  onSubmit,
  isNewStudent,
  saveLabel,
}) => {
  const user = useContext(UserContext);
  const [inviteUser, { error: inviteError }] = useMutation(INVITE_USER);
  const [removeParent] = useMutation(REMOVE_PARENT);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [studentForm, setStudentForm] = useState(student);

  const handleChange = (e, { name, value }) =>
    setStudentForm({ ...studentForm, [name]: value });

  const { name, reportFrequency } = studentForm;

  const isValid = name.length > 0;

  return (
    <Form
      onSubmit={(evt) => {
        evt.preventDefault();
        const updatedStudent = Object.assign({}, studentForm, {
          parents: student.parents,
        });

        onSubmit(getStudentInput(updatedStudent));
      }}
    >
      <Form.Input
        fluid
        label="Student Name"
        placeholder="Jane Doe"
        name="name"
        value={name}
        disabled={student.archived}
        onChange={handleChange}
      />
      <Form.Select
        fluid
        label="Reporting Frequency"
        options={frequencies}
        name="reportFrequency"
        value={reportFrequency}
        disabled={student.archived}
        onChange={handleChange}
      />
      <Form.Field disabled={student.archived}>
        <label>Parents/Guardians</label>
        {isNewStudent ? (
          <span>Parents can be added once the student is saved.</span>
        ) : (
          <SimpleUserList
            users={student.parents}
            addLabel="Add Parent/Guardian"
            disabled={student.archived}
            error={inviteError}
            onRemoveUser={(user, index) =>
              removeParent({
                variables: { student: student.id, index },
                update: (cache, { data: { removeParent } }) => {
                  cache.modify({
                    id: cache.identify(student),
                    fields: {
                      parents(existingParentRefs = [], { readField }) {
                        return existingParentRefs.filter(
                          (r) => readField("sub", r) !== removeParent.sub
                        );
                      },
                    },
                  });
                },
              })
            }
            onAddUser={(email) =>
              inviteUser({
                variables: { group: "Parents", id: student.id, email },
                update: (cache, { data: { inviteUser } }) => {
                  cache.modify({
                    id: cache.identify(student),
                    fields: {
                      parents(existingParentRefs = [], { readField }) {
                        const newUserRef = cache.writeFragment({
                          data: inviteUser,
                          fragment: gql`
                            fragment NewUser on User {
                              email
                              status
                              sub
                            }
                          `,
                        });

                        return [...existingParentRefs, newUserRef];
                      },
                    },
                  });
                },
              })
            }
          />
        )}
      </Form.Field>

      <Form.Group>
        <Form.Button type="submit" basic primary disabled={!isValid}>
          {saveLabel}
        </Form.Button>
        <Form.Button type="button" basic onClick={onCancel}>
          Cancel
        </Form.Button>
        {user.canDeleteStudents && isNewStudent === false ? (
          <Form.Button
            type="button"
            basic
            color="red"
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Delete Student
          </Form.Button>
        ) : null}
        {user.canEditStudents && isNewStudent === false && !student.archived ? (
          <Form.Button
            type="button"
            basic
            onClick={() => setArchiveConfirmOpen(true)}
          >
            Archive Student
          </Form.Button>
        ) : null}
      </Form.Group>
      <Confirm
        open={archiveConfirmOpen}
        onCancel={() => setArchiveConfirmOpen(false)}
        onConfirm={() => {
          setArchiveConfirmOpen(false);
          const updatedStudent = Object.assign({}, student, { archived: true });
          onSubmit(getStudentInput(updatedStudent));
        }}
        confirmButton="Archive"
        content={`Archiving a student will remove the student from your list of active students and prevent further updates. Archive this student?`}
        header="Archive Student"
      />
      <Confirm
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          setDeleteConfirmOpen(false);
          onDelete();
        }}
        confirmButton="Delete"
        content={`Are you sure you want to delete ${student.name}?`}
        header="Delete Student"
      />
    </Form>
  );
};

StudentForm.defaultProps = {
  saveLabel: "Save",
};

export default StudentForm;
